/**
 * Revenue Tracker Component (Issue #679)
 *
 * Enables vendors to log and manage fair-related revenue for ROI calculation.
 * Supports multiple revenue sources.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { revenueApi } from '../services/api';
import { confirmAsync } from '../utils/toast';
import SimplePagination from './SimplePagination';
import { addCurrency, formatCurrency, parseCurrencyInput } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Type-safe error handling
import {
  VendorFairRevenue,
  VendorFairRevenueCreate,
  RevenueSource,
  REVENUE_SOURCE_LABELS,
  PaginatedRevenueResponse
} from '../types';

interface RevenueTrackerProps {
  vendorId: number;
  fairId: number;
  onRevenueAdded?: () => void;
}

export const RevenueTracker: React.FC<RevenueTrackerProps> = ({
  vendorId,
  fairId,
  onRevenueAdded
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [revenues, setRevenues] = useState<VendorFairRevenue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Pagination state (Issue #1180)
  // Initialize from URL query params with validation
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    if (!pageParam) return 1;

    const page = parseInt(pageParam, 10);
    // Validate: must be positive integer, reasonable max
    if (isNaN(page) || page < 1 || page > 10000) {
      console.warn(`Invalid page param: ${pageParam}, defaulting to 1`);
      return 1;
    }
    return page;
  });
  const [pageSize, setPageSize] = useState(() => {
    // Priority: URL param > localStorage > default (20)
    const sizeParam = searchParams.get('pageSize');
    const validSizes = [10, 20, 50, 100];

    if (sizeParam) {
      const size = parseInt(sizeParam, 10);
      if (!isNaN(size) && validSizes.includes(size)) {
        return size;
      }
      console.warn(`Invalid pageSize param: ${sizeParam}, checking localStorage`);
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem('vendor_revenue_pageSize');
      if (stored) {
        const size = parseInt(stored, 10);
        if (!isNaN(size) && validSizes.includes(size)) {
          return size;
        }
      }
    } catch (e: unknown) {
      // localStorage might be disabled
      console.warn('Failed to read from localStorage:', e);
    }

    return 20; // Default (Issue #1180)
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Form state
  const [revenueSource, setRevenueSource] = useState<RevenueSource>('product_sales');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  // Update URL query params when page or pageSize changes (Issue #1180)
  useEffect(() => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);

      if (currentPage > 1) {
        newParams.set('page', currentPage.toString());
      } else {
        newParams.delete('page');
      }

      if (pageSize !== 20) {
        newParams.set('pageSize', pageSize.toString());
      } else {
        newParams.delete('pageSize');
      }

      return newParams;
    }, { replace: true });
  }, [currentPage, pageSize, setSearchParams]);

  // Type guard for paginated response (Issue #768 code review)
  const isPaginatedRevenueResponse = (
    data: VendorFairRevenue[] | PaginatedRevenueResponse
  ): data is PaginatedRevenueResponse => {
    return 'pagination' in data && typeof (data as PaginatedRevenueResponse).pagination === 'object';
  };

  // Load revenues with pagination (Issue #768)
  const loadRevenues = useCallback(async () => {
    try {
      setLoading(true);
      const data = await revenueApi.getRevenue(vendorId, fairId, currentPage, pageSize);

      // Handle paginated response using type guard
      if (isPaginatedRevenueResponse(data)) {
        setRevenues(data.items);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
      } else {
        // Backward compatible: non-paginated response
        setRevenues(data);
        setTotalPages(1);
        setTotalCount(data.length);
      }

      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error loading revenue:', apiError);
    } finally {
      setLoading(false);
    }
  }, [vendorId, fairId, currentPage, pageSize]);

  useEffect(() => {
    loadRevenues();
  }, [loadRevenues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Issue #1160: Use parseCurrencyInput for precision-safe validation
    const parsedAmount = parseCurrencyInput(amount);
    if (!amount || parsedAmount === '0.00') {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      // Issue #1160: Send amount as string for Decimal precision (Issue #890)
      const revenue: VendorFairRevenueCreate = {
        vendor_id: vendorId,
        fair_id: fairId,
        revenue_source: revenueSource,
        amount: parsedAmount, // Already string from parseCurrencyInput
        date,
        notes: notes || undefined
      };

      await revenueApi.createRevenue(vendorId, fairId, revenue);

      // Reset form
      setAmount('');
      setNotes('');
      setShowForm(false);

      // Reload revenues (reset to page 1 if needed)
      if (currentPage !== 1) {
        setCurrentPage(1); // This will trigger useEffect to reload
      } else {
        await loadRevenues(); // Already on page 1, just reload
      }

      if (onRevenueAdded) {
        onRevenueAdded();
      }

      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error creating revenue:', apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (revenueId: number) => {
    const confirmed = await confirmAsync('Are you sure you want to delete this revenue entry?');
    if (!confirmed) return;

    try {
      setLoading(true);
      await revenueApi.deleteRevenue(vendorId, revenueId);

      // Reload current page to get fresh data (Issue #768 code review - fix race condition)
      const data = await revenueApi.getRevenue(vendorId, fairId, currentPage, pageSize);

      // Check if current page is now empty AND we're not on page 1
      if (isPaginatedRevenueResponse(data) && data.items.length === 0 && currentPage > 1) {
        // Navigate to previous page
        setCurrentPage(currentPage - 1);
      } else {
        // Update state with fresh data using type guard
        if (isPaginatedRevenueResponse(data)) {
          setRevenues(data.items);
          setTotalPages(data.pagination.total_pages);
          setTotalCount(data.pagination.total_count);
        } else {
          setRevenues(data);
          setTotalPages(1);
          setTotalCount(data.length);
        }
      }

      if (onRevenueAdded) {
        onRevenueAdded();
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error deleting revenue:', apiError);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 when changing page size

    // Persist to localStorage for future sessions (Issue #1180)
    try {
      localStorage.setItem('vendor_revenue_pageSize', newPageSize.toString());
    } catch (e: unknown) {
      console.warn('Failed to save page size to localStorage:', e);
    }

    // Note: loadRevenues will be triggered by useEffect when currentPage changes
  };

  // Issue #1160: Use Decimal.js addCurrency for precision-safe addition
  const totalRevenue = addCurrency(...revenues.map(rev => rev.amount));

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Revenue Tracker</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
          data-testid="add-revenue-button"
        >
          {showForm ? 'Cancel' : '+ Add Revenue'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add Revenue Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Revenue Source *
              </label>
              <select
                value={revenueSource}
                onChange={(e) => setRevenueSource(e.target.value as RevenueSource)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                required
              >
                {Object.entries(REVENUE_SOURCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount * ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                placeholder="0.00"
                required
                data-testid="revenue-amount-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                rows={2}
                placeholder="Optional notes..."
                data-testid="revenue-description-input"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50"
              data-testid="save-revenue-button"
            >
              {loading ? 'Saving...' : 'Save Revenue'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Revenues List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Revenue Entries
          </h3>
          <div className="text-lg font-bold text-green-700" data-testid="total-revenue">
            {formatCurrency(totalRevenue)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>

        {loading && !showForm ? (
          <div className="text-center py-8 text-gray-500">Loading revenue...</div>
        ) : revenues.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No revenue recorded yet. Click "Add Revenue" to get started.
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {revenues.map((revenue) => (
                <div
                  key={revenue.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid="revenue-list-item"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-900">
                          {REVENUE_SOURCE_LABELS[revenue.revenue_source]}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(revenue.date).toLocaleDateString()}
                        </span>
                      </div>
                      {revenue.notes && (
                        <p className="text-sm text-gray-600">{revenue.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold text-green-700">
                        {formatCurrency(revenue.amount)} {/* Issue #905: Decimal.js formatting */}
                      </span>
                      <button
                        onClick={() => handleDelete(revenue.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination (Issue #1180) */}
            <SimplePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={loading}
              ariaLabel="Revenue list pagination"
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100]}
              onPageSizeChange={handlePageSizeChange}
              totalCount={totalCount}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RevenueTracker;
